import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
import { hasItems } from "~/features/app/utils";
import { type Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { type CallToActionListItemViewProps } from "~/features/blocks/inventory/call-to-action-list/call-to-action-list-item";
import { takeFirst } from "~/features/app/utils";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type CallToActionListBlockViewProps<TItem = Omit<CallToActionListItemViewProps, "layout" | "showLinks">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  items?: TItem[] | null;
  mode?: string | null;
  renderItem: (item: TItem, index: number, layout: "horizontal" | "vertical") => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  showLinks?: boolean | null;
  theme?: string | null;
};
export function CallToActionListBlockView<TItem = Omit<CallToActionListItemViewProps, "layout" | "showLinks">>({
  anchor,
  getItemKey,
  hasTopDivider = true,
  items = [],
  mode = "light",
  renderItem,
  sectionProps,
  theme = "white"
}: CallToActionListBlockViewProps<TItem>) {
  const groups = items ?? [];
  if (!hasItems(groups)) {
    return null;
  }
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px",
    "borderTopColor": "var(--stroke-primary)"
  },
  // v3: white section with #EFEFF6 (grey-20) cards.
  {
    "position": "relative",
    "backgroundColor": "var(--bg-primary)"
  }]}>
      <_StyledWrap mode={mode as Mode}>
        {/* Desktop side padding (grid margin) keeps the page pattern visible next to the cards. */}
        <_StyledUl $_css2={[{
        "position": "relative",
        "zIndex": "10",
        "display": "flex",
        "flexDirection": "column",
        "gap": "var(--spacing-md)",
        "paddingTop": "var(--spacing-md)",
        "paddingBottom": "var(--spacing-md)",
        "@media (min-width: 1024px)": {
          "flexDirection": "row",
          "paddingLeft": "var(--grid-v3-margin)",
          "paddingRight": "var(--grid-v3-margin)",
          "paddingTop": "var(--spacing-xl)",
          "paddingBottom": "var(--spacing-xl)"
        }
      }]}>
          {groups.map((item, idx) => <_StyledLi key={getItemKey?.(item, idx) || idx} $_css3={[{
          "width": "100%",
          "overflow": "hidden",
          "borderRadius": "4px",
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(239 239 246 / var(--tw-bg-opacity))"
        }]}>
              {renderItem(item, idx, groups.length > 1 ? "vertical" : "horizontal")}
            </_StyledLi>)}
        </_StyledUl>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-5vqpxg-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-5vqpxg-1"
})({
  "borderWidth": "0px"
});
var _StyledUl = _styled3("ul").withConfig({
  componentId: "sc-sf-5vqpxg-2"
})`${p => p.$_css2}`;
var _StyledLi = /*#__PURE__*/_styled4("li").withConfig({
  displayName: "call-to-action-list___StyledLi",
  componentId: "sc-2mwhvw-0"
})(["", ""], p => p.$_css3);